import { useState, useCallback, useMemo } from 'react'
import {
  IPaymentEntry,
  ILocalPaymentEntry,
  PaymentMethod,
} from '@/supabase/DailySalesSummary/dailySalesSummary.interface'
import { batchSavePaymentEntries } from '@/supabase/DailySalesSummary/paymentEntry'

interface RowKeyMeta {
  customerId: string
  orderBookId?: number
}

export interface UsePaymentEntriesReturn {
  localPaymentEntries: Map<string, ILocalPaymentEntry[]> // key = rowKey
  addPaymentEntry: (rowKey: string) => string
  updatePaymentEntry: (rowKey: string, tempId: string, updates: Partial<ILocalPaymentEntry>) => void
  deletePaymentEntry: (rowKey: string, tempId: string) => void
  saveAllChanges: (date: string) => Promise<boolean>
  hasChanges: boolean
  saving: boolean
  initializeFromServer: (rowKey: string, customerId: string, orderBookId: number | undefined, entries: IPaymentEntry[]) => void
  registerRowKey: (rowKey: string, customerId: string, orderBookId?: number) => void
  clearAllEntries: () => void
}

export const usePaymentEntries = (): UsePaymentEntriesReturn => {
  const [localPaymentEntries, setLocalPaymentEntries] = useState<Map<string, ILocalPaymentEntry[]>>(new Map())
  const [rowKeyMetaMap, setRowKeyMetaMap] = useState<Map<string, RowKeyMeta>>(new Map()) // rowKey → {customerId, orderBookId}
  const [saving, setSaving] = useState(false)

  // Add a new payment entry for a row
  // Requirements: 2.4
  const addPaymentEntry = useCallback((rowKey: string): string => {
    const meta = rowKeyMetaMap.get(rowKey)
    const newTempId = crypto.randomUUID()
    setLocalPaymentEntries(prev => {
      const newMap = new Map(prev)
      const entries = [...(newMap.get(rowKey) || [])]
      entries.push({
        tempId: newTempId,
        orderBookId: meta?.orderBookId,
        amountTHB: 0,
        paymentMethod: PaymentMethod.CASH,
        status: 'new',
      })
      newMap.set(rowKey, entries)
      return newMap
    })
    return newTempId
  }, [rowKeyMetaMap])

  // Update an existing payment entry
  // Requirements: 3.1
  const updatePaymentEntry = useCallback(
    (rowKey: string, tempId: string, updates: Partial<ILocalPaymentEntry>) => {
      setLocalPaymentEntries(prev => {
        const newMap = new Map(prev)
        const entries = newMap.get(rowKey)
        if (!entries) return prev

        const updatedEntries = entries.map(entry => {
          if (entry.tempId !== tempId) return entry
          const newStatus = entry.status === 'unchanged' ? 'modified' as const : entry.status
          return { ...entry, ...updates, status: newStatus }
        })
        newMap.set(rowKey, updatedEntries)
        return newMap
      })
    },
    []
  )

  // Delete a payment entry
  // Requirements: 4.1
  const deletePaymentEntry = useCallback((rowKey: string, tempId: string) => {
    setLocalPaymentEntries(prev => {
      const newMap = new Map(prev)
      const entries = newMap.get(rowKey)
      if (!entries) return prev

      const updatedEntries: ILocalPaymentEntry[] = []
      for (const entry of entries) {
        if (entry.tempId !== tempId) {
          updatedEntries.push(entry)
          continue
        }
        // If entry is 'new' (never saved to server), remove it entirely
        if (entry.status === 'new') {
          continue
        }
        // If entry exists on server, mark as 'deleted'
        updatedEntries.push({ ...entry, status: 'deleted' })
      }
      newMap.set(rowKey, updatedEntries)
      return newMap
    })
  }, [])

  // Save all changes to the server
  // Requirements: 5.1, 5.3, 5.4
  const saveAllChanges = useCallback(async (date: string): Promise<boolean> => {
    setSaving(true)
    try {
      let allSucceeded = true

      for (const [rowKey, entries] of localPaymentEntries) {
        const adds = entries
          .filter(e => e.status === 'new')
          .map(e => ({
            orderBookId: e.orderBookId,
            amountTHB: e.amountTHB,
            paymentMethod: e.paymentMethod,
            note: e.note,
          }))

        const updates = entries
          .filter(e => e.status === 'modified' && e.id)
          .map(e => ({
            id: e.id!,
            amountTHB: e.amountTHB,
            paymentMethod: e.paymentMethod,
            note: e.note,
          }))

        const deletes = entries
          .filter(e => e.status === 'deleted' && e.id)
          .map(e => e.id!)

        // Skip rows with no changes
        if (adds.length === 0 && updates.length === 0 && deletes.length === 0) {
          continue
        }

        // Resolve meta from the rowKey → meta mapping
        const meta = rowKeyMetaMap.get(rowKey)
        const customerId = meta?.customerId || rowKey
        const orderBookId = meta?.orderBookId

        const success = await batchSavePaymentEntries({
          customerId,
          date,
          orderBookId,
          adds,
          updates,
          deletes,
        })

        if (!success) {
          allSucceeded = false
          break
        }
      }

      // Don't clear localPaymentEntries here — let refreshData() + initializeFromServer
      // overwrite with fresh server data to avoid a flash of stale values.

      return allSucceeded
    } catch (error) {
      console.error('Error in saveAllChanges:', error)
      return false
    } finally {
      setSaving(false)
    }
  }, [localPaymentEntries, rowKeyMetaMap])

  // Check if there are any unsaved changes
  const hasChanges = useMemo(() => {
    for (const entries of localPaymentEntries.values()) {
      for (const entry of entries) {
        if (entry.status !== 'unchanged') {
          return true
        }
      }
    }
    return false
  }, [localPaymentEntries])

  // Initialize local state from server data
  const initializeFromServer = useCallback((rowKey: string, customerId: string, orderBookId: number | undefined, entries: IPaymentEntry[]) => {
    // Store the rowKey → meta mapping for use during save
    setRowKeyMetaMap(prev => {
      const newMap = new Map(prev)
      newMap.set(rowKey, { customerId, orderBookId })
      return newMap
    })

    setLocalPaymentEntries(prev => {
      const newMap = new Map(prev)
      // Only initialize if not already in local state (preserve user edits)
      if (newMap.has(rowKey)) return newMap
      const localEntries: ILocalPaymentEntry[] = entries.map(entry => ({
        tempId: entry.id,  // Use server ID as stable tempId to prevent input remount
        id: entry.id,
        dailyCustomerSummaryId: entry.dailyCustomerSummaryId,
        orderBookId: entry.orderBookId,
        amountTHB: entry.amountTHB,
        paymentMethod: entry.paymentMethod,
        note: entry.note,
        status: 'unchanged' as const,
      }))
      newMap.set(rowKey, localEntries)
      return newMap
    })
  }, [])

  // Register a rowKey → meta mapping (for rows without existing payment entries)
  const registerRowKey = useCallback((rowKey: string, customerId: string, orderBookId?: number) => {
    setRowKeyMetaMap(prev => {
      if (prev.has(rowKey)) return prev
      const newMap = new Map(prev)
      newMap.set(rowKey, { customerId, orderBookId })
      return newMap
    })
  }, [])

  // Clear all local payment entries (used before re-initializing from server)
  const clearAllEntries = useCallback(() => {
    setLocalPaymentEntries(new Map())
    setRowKeyMetaMap(new Map())
  }, [])

  return {
    localPaymentEntries,
    addPaymentEntry,
    updatePaymentEntry,
    deletePaymentEntry,
    saveAllChanges,
    hasChanges,
    saving,
    initializeFromServer,
    registerRowKey,
    clearAllEntries,
  }
}
